import { Recurrence, SelfServiceSchedulePane, StaticInitializers } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { ButtonType, DialogFooterButtonsWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { classes } from 'typestyle';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { IExtractTaskInfo } from '~src/extractTask/models/IExtractTaskInfo';
import { ExtractTaskType } from '~src/extractTask/serverApi/types/IExtractTask';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { PublishedConnectionExtractRefreshLevelOptions } from '~src/publishedConnection/components/scheduledTasks/PublishedConnectionExtractRefreshLevelOptions';
import { IPublishedConnectionDetailInfo } from '~src/publishedConnection/models/IPublishedConnectionInfo';
import {
  getPublishedConnectionExtractRefreshLevel,
  getUserTimeZoneDetails,
  isExtractSSSEnabled
} from '~src/publishedConnection/utils/PublishedConnectionTaskUtil';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { cssClass } from '~src/style';

import { PublishedConnections } from '../../resources/PublishedConnections';
import { PublishedConnectionExtractRefreshLevel } from '../../types/PublishedConnectionExtractRefreshLevel';
import { PublishedConnectionTaskTableInfo } from '../../types/PublishedConnectionTaskTableInfo';
import { PublishedConnectionScheduledTaskTableSelectionDialog } from './PublishedConnectionScheduledTaskTableSelectionDialog';
import { PublishedConnectionScheduledTaskTablesGrid } from './PublishedConnectionScheduledTaskTablesGrid';
import { PublishedConnectionTaskRefreshTypeDropdown } from './PublishedConnectionTaskRefreshTypeDropdown';
import { PublishedConnectionTaskSchedulePicker } from './PublishedConnectionTaskSchedulePicker';

const cssPrefix = 'PublishedConnectionCreateTaskDialogContent';

const rootClass = cssClass(cssPrefix, 'root', {
  display: 'flex',
  flexDirection: 'column'
});

const topPaneClass = cssClass(cssPrefix, 'topPane', {
  flex: 'none'
});

const dialogMessageClass = cssClass(cssPrefix, 'dialogMessage', {
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const paddingBottomClass = cssClass(cssPrefix, 'paddingBottom', {
  paddingBottom: CommonStyles.Sizes.LargeSpace,
});

const refreshTypeButtonClass = cssClass(cssPrefix, 'refreshTypeButton', {
  width: '250px'
});

const bottomPaneClass = cssClass(cssPrefix, 'bottomPane', {
  flex: 1,
  flexDirection: 'column'
});

const footerClass = cssClass(cssPrefix, 'footerClass', {
  flex: 'none',
});

const tablesContainerClass = cssClass(cssPrefix, 'tablesContainer', {
  borderLeft: `1px solid ${CommonStyles.Colors.Grey203}`,
  borderRight: `1px solid ${CommonStyles.Colors.Grey203}`,
  borderBottom: `1px solid ${CommonStyles.Colors.Grey203}`,
  marginBottom: TabStyles.Spacing.LargeSpace,
  flex: 1,
  overflow: 'auto',
  height: 250
});

const editTableLinkClass = cssClass(cssPrefix, 'editTableLink', {
  paddingBottom: TabStyles.Spacing.DefaultSpace,
  fontWeight: 'bold'
});

const frequencyHeaderClass = cssClass(cssPrefix, 'frequencyHeader', {
  fontWeight: 'normal',
  fontSize: 16,
  margin: '0',
  height: '20px',
  lineHeight: '20px',
  paddingBottom: '8px',
});

const frequencyPaneClass = cssClass(cssPrefix, 'frequencyPane', {
  maxWidth: 500,
  marginTop: '-36px',
  marginBottom: '18px'
});

const sortTablesByName = (a: PublishedConnectionTaskTableInfo, b: PublishedConnectionTaskTableInfo) => (a.alias > b.alias) ? 1 : -1;

function renderSelfServiceSchedulePane(
  recurrence: Recurrence,
  onRecurrenceChange: (model: Recurrence) => void,
  dismiss: () => void
) {

  return (
    <>
      <h1 className={frequencyHeaderClass}>{Messages.refreshSelfServiceScheduleRefreshFrequency()}</h1>
      <span data-tb-test-id='schedule-published-connection-recurrence-description'>
          {IScheduleInfo.getRecurrenceDescription(recurrence)}
        </span>
      <div className={frequencyPaneClass}>
        <SelfServiceSchedulePane
          recurrenceModel={recurrence}
          recurrenceModelChangedCallback={onRecurrenceChange}
          timeZoneDetails={getUserTimeZoneDetails()}
          type={SelfServiceSchedulePaneType.Default}
          uncollapsible={true}
          onTimeZoneClick={dismiss}
        />
      </div>
    </>
  );
}

function renderAdminSchedulePane(
  selectedSchedule: string,
  onSelectSchedule: (value: string) => void
) {

  return (
    <>
      <div className={dialogMessageClass}>
        {Messages.schedulePublishedConnectionChooseScheduleMessage()}
      </div>

      <div className={paddingBottomClass}>
        <PublishedConnectionTaskSchedulePicker
          scheduleId={selectedSchedule}
          onSelect={onSelectSchedule}
        />
      </div>
    </>
  );
}

function handleResult(result: IResult, close: () => void, isNew?: boolean) {
  const successMessage = isNew ? Messages.createPublishedConnectionTaskSuccessful() : Messages.editPublishedConnectionTaskSuccessful();
  let failureMessage = undefined;
  if (result.errors && result.errors.length > 0) {
    failureMessage = Messages.refreshSelfServiceScheduleUnexpectedError({ ERROR_CODE: String(result.errors[0].code) });
  }
  PublishedConnections.toast(result, successMessage, failureMessage);
  if (!failureMessage) {
    close();
  }
}

export type PublishedConnectionCreateOrEditTaskDialogContentProps = Readonly<{
  close: () => void;
  dismiss: () => void;
  publishedConnectionInfo: IPublishedConnectionDetailInfo;
  taskInfo?: IExtractTaskInfo;
}>;

export function PublishedConnectionCreateOrEditTaskDialogContent(props: PublishedConnectionCreateOrEditTaskDialogContentProps): JSX.Element {
  const { close, dismiss, publishedConnectionInfo, taskInfo } = props;

  const [selectedSchedule, setSelectedSchedule] = React.useState<string | undefined>(taskInfo?.schedule.id);
  const [extractRefreshLevel, setExtractRefreshLevel] = React.useState<PublishedConnectionExtractRefreshLevel>(
    getPublishedConnectionExtractRefreshLevel(taskInfo?.extractServiceTask?.transactionLevel)
  );
  const [editTablesDialogVisible, setEditTablesDialogVisibility] = React.useState(false);
  const [selectedTableLuids, setSelectedTableLuids] = React.useState<ReadonlyArray<string>>(taskInfo?.extractServiceTask?.startingItemIds || []);
  const anchorRefForTableSelectionDialog = React.useRef(null);

  const availableTables = React.useMemo(
    () => publishedConnectionInfo.tables.map(table => ({
      alias: table.alias,
      luid: table.luid
    })).sort(sortTablesByName),
    [publishedConnectionInfo.tables]
  );
  const selfServiceEnabled = isExtractSSSEnabled();

  const isGoButtonDisabled = React.useMemo(
    () => (!selfServiceEnabled && !selectedSchedule) || selectedTableLuids.length === 0,
    [selectedSchedule, selectedTableLuids.length, selfServiceEnabled]);
  const handleSelectSchedule = React.useCallback((value) => setSelectedSchedule(value), [setSelectedSchedule]);
  const handleRadioSelect = React.useCallback((value) => setExtractRefreshLevel(value), [setExtractRefreshLevel]);

  const [recurrence, setRecurrence] = React.useState<Recurrence>(taskInfo?.schedule
    ? IScheduleInfo.toSSSRecurrence(taskInfo.schedule)
    : StaticInitializers.defaultRecurrence());


  const handleCreateTask = React.useCallback(
    () => {
      if (selfServiceEnabled) {
        PublishedConnections.createExtractTaskSelfServiceSchedule(
          parseInt(publishedConnectionInfo.id),
          recurrence,
          selectedTableLuids,
          'ExtractServiceFullRefresh',
          extractRefreshLevel
        ).then(result => handleResult(result, close, true));
      } else {
        PublishedConnections.createExtractTask(
          publishedConnectionInfo.id,
          selectedSchedule,
          selectedTableLuids,
          ExtractTaskType.ExtractServiceFullRefresh,
          extractRefreshLevel
        ).then(result => handleResult(result, close, true));
      }
    },
    [close, extractRefreshLevel, publishedConnectionInfo.id, recurrence, selectedSchedule, selectedTableLuids, selfServiceEnabled]
  );

  const handleEditTask = React.useCallback(
    () => {
      if (!taskInfo) {
        return;
      }
      if (selfServiceEnabled) {
        PublishedConnections.updateExtractTaskSelfServiceSchedule(
          parseInt(taskInfo.id),
          recurrence,
          selectedTableLuids,
          'ExtractServiceFullRefresh',
          extractRefreshLevel
        ).then(result => handleResult(result, close));
      } else {

        PublishedConnections.updateExtractTask(
          taskInfo.id,
          selectedSchedule,
          selectedTableLuids,
          ExtractTaskType.ExtractServiceFullRefresh,
          extractRefreshLevel
        ).then(result => handleResult(result, close));
      }
    },
    [close, extractRefreshLevel, recurrence, selectedSchedule, selectedTableLuids, selfServiceEnabled, taskInfo]
  );

  const handleSelectTables = React.useCallback(
    (tables: ReadonlyArray<string>) => {
      setSelectedTableLuids(tables);
      setEditTablesDialogVisibility(false);
    },
    [setEditTablesDialogVisibility]
  );

  const cancelSelectTables = React.useCallback(
    () => setEditTablesDialogVisibility(false),
    [setEditTablesDialogVisibility]
  );

  const toggleEditTablesDialogVisibility = React.useCallback(
    () => setEditTablesDialogVisibility(!editTablesDialogVisible),
    [editTablesDialogVisible, setEditTablesDialogVisibility]
  );

  const selectedTables = React.useMemo((): ReadonlyArray<PublishedConnectionTaskTableInfo> => {
      const availableTablesMap = _.indexBy(availableTables, 'luid');
      return selectedTableLuids.map(table => availableTablesMap[table]);
    },
    [availableTables, selectedTableLuids]
  );

  const handleRecurrenceChange = React.useCallback((newRecurrence) => setRecurrence(newRecurrence), [setRecurrence]);

  return (
    <div className={rootClass}>
      <div className={topPaneClass}>
        {selfServiceEnabled
          ? renderSelfServiceSchedulePane(recurrence, handleRecurrenceChange, dismiss)
          : renderAdminSchedulePane(selectedSchedule, handleSelectSchedule)
        }
        <PublishedConnectionExtractRefreshLevelOptions selected={extractRefreshLevel} onChange={handleRadioSelect}/>

        <div className={classes(refreshTypeButtonClass, paddingBottomClass)}>
          <PublishedConnectionTaskRefreshTypeDropdown/>
        </div>

        <div className={editTableLinkClass}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a ref={anchorRefForTableSelectionDialog}
             onClick={toggleEditTablesDialogVisibility}
             href='javascript:void(0)'>
            {Messages.publishedConnectionScheduledTaskAddEditTables()}
          </a>
        </div>

        {editTablesDialogVisible && (
          <PublishedConnectionScheduledTaskTableSelectionDialog
            anchorElementRef={anchorRefForTableSelectionDialog.current}
            onCancel={cancelSelectTables}
            onSelect={handleSelectTables}
            availableTables={availableTables}
            selectedTableLuids={selectedTableLuids}
          />
        )}

      </div>
      <div className={bottomPaneClass}>
        <div className={tablesContainerClass}>
          <PublishedConnectionScheduledTaskTablesGrid
            emptyGridMessage={Messages.publishedConnectionScheduledTaskNoTablesMessage()}
            tables={selectedTables}
          />
        </div>

        <div className={footerClass}>
          <DialogFooterButtonsWidget
            buttons={[{
              handleClick: dismiss,
              buttonType: ButtonType.Outline,
              children: Messages.cancel(),
              testId: 'schedule-published-connection-task-cancel'
            }, {
              handleClick: taskInfo ? handleEditTask : handleCreateTask,
              buttonType: ButtonType.Primary,
              children: taskInfo ? Messages.editTask() : Messages.schedulePublishedConnectionTask(),
              testId: 'schedule-published-connection-task-go',
              disabled: isGoButtonDisabled
            }]}
            testId={'schedule-published-connection-task-footer'}
          />
        </div>
      </div>
    </div>
  );
}
